import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Divider, Modal, Spin } from "antd";
import NGISelect from "components/Common/inputs/NGISelect";
import { Option } from "antd/lib/mentions";
import CustomButton from "components/Common/buttons/CustomButton";
import {
  completeTask,
  delayTaskApi,
  fetchCustomerById,
  retrieveTasksArchive,
  setCustomerAvailabilityV2,
} from "utils/api";
import NGIInput from "components/Common/inputs/NGIInput";
import { makeStyles } from "@material-ui/core/styles";
import NGIFormItem from "components/Common/form/NGIFormItem";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import phoneIcon from "assets/images/svg/phoneIcon.svg";
import NGIForm from "components/Common/form/NGIForm";
import CustomLink from "components/Common/buttons/CustomLink";
import { daysToToday, formatString } from "utils/helpers/functions";
import { AUTH_USER } from "utils/constants";
import {
  computeSurveyPath,
  getDownstreamStepIds,
  getSurveyFieldName,
  getTerminalBranch,
  getVisibleUserSteps,
  CARD_LOST_BRANCH_ID,
} from "./surveyEngine";
import { VALIDATION_RULES } from "pages/SMS-management/SMS-bulk/NewBulkSMS/constants";
import { useForm, useWatch } from "antd/es/form/Form";
import { getStartOfTheDay } from "components/Common/formatter";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";
import CalendarPicker from "pages/customer-management/customerDetails/CalendarPicker";

const useStyles = makeStyles({
  titleWithDescription: {
    display: "flex",
    flexDirection: "column",
    textAlign: "left",
    fontWeight: 600,

    "&>span": {
      fontSize: 13,
      fontWeight: 300,
    },
  },
  modalContent: {
    padding: "15px 0",
    border: "1px solid rgba(0, 0, 0, 0.05)",
    borderLeft: "none",
    borderRight: "none",
    marginBottom: 24,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  actionsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",

    "&>div": {
      "& .button-general + .button-general": {
        marginLeft: 20,
      },
    },
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "5px",
  },
  customerSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#0085FF0D",
    padding: 15,
    borderRadius: 5,
    marginBottom: 30,
  },
  customerName: {
    fontSize: 16,
    fontWeight: 700,
  },
  link: {
    color: "#0085FF!important",
  },
  customerPhone: {
    fontWeight: 700,
    "&>img": {
      marginRight: 5,
    },
  },
  fieldsContainer: {},
  fieldItem: {
    display: "flex",
    flexDirection: "row",
  },
  fieldQuestion: {
    width: "65%",
  },
  fieldQuestionTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  fieldAnswer: {
    flex: 1,
    maxWidth: 245,
  },
  taskPreviewContainer: {
    borderRadius: 3,
    border: "1px solid rgba(0, 0, 0, 0.05)",
    background: "#FAFAFA",
    padding: 15,
    paddingBottom: 0,
    marginBottom: 20,
  },
  taskPreviewName: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 10,
  },
  taskSettings: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  taskSettingsAvailability: {
    width: "100%",
  },
  availabilityContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    marginTop: 10,
  },
  dateContainer: {
    flex: 1,
  },
  timeContainer: {
    display: "flex",
    gap: 10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 10,
  },
  taskSettingsCardLost: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  section: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  warnContainer: {
    fontSize: 12,
    fontWeight: 300,
    marginBottom: 10,
    padding: 10,
    background: "linear-gradient(180deg, #FFFFFF 0%, #FFFBC1 100%)",
    borderRadius: 5,
  },
  dayBox: {
    width: 92,
  },
  calendarPicker: {
    marginBottom: 15,
  },
});

const ExecuteTaskModal = ({ onClose, update, data, interventionCallSurvey }) => {
  const classes = useStyles();
  const [form] = useForm();
  const [isLoading, setLoader] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [lastInvestigationDate, setLastInvestigationDate] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isConfirmatioCompletenOpen, setIsConfirmatioCompletenOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const openConfirmationModal = useCallback(e => {
    e?.preventDefault();
    setIsConfirmationOpen(true);
  }, []);
  const closeConfirmationModal = useCallback(() => setIsConfirmationOpen(false), []);

  const openConfirmationCompleteModal = useCallback(() => setIsConfirmatioCompletenOpen(true), []);
  const closeConfirmationCompleteModal = useCallback(() => setIsConfirmatioCompletenOpen(false), []);

  const userFullName = useMemo(() => {
    const authUser = AUTH_USER();
    return formatString([authUser?.firstName, authUser?.lastName].filter(Boolean).join(" "));
  }, []);

  const formValues = useWatch([], form);
  const isWillPayImmediately = useWatch("survey.isWillPayImmediately", form);

  const getCustomerAge = useCallback(() => {
    switch (customer?.status) {
      case "Repossessed":
        return daysToToday(customer?.installationDate, customer?.terminatedAt);
      default:
        return daysToToday(customer?.installationDate);
    }
  }, [customer]);

  const hasRecentInvestigationOrIntervention = useMemo(() => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const oneMonthAgoTimestamp = oneMonthAgo.getTime();

    return customer?.lastInterventionAt >= oneMonthAgoTimestamp || lastInvestigationDate >= oneMonthAgoTimestamp;
  }, [customer?.lastInterventionAt, lastInvestigationDate]);

  const surveyAnswers = useMemo(() => {
    const answers = {};

    interventionCallSurvey.steps.forEach(step => {
      const value = formValues?.[getSurveyFieldName(step.id)];
      if (value) {
        answers[step.id] = value;
      }
    });

    return answers;
  }, [formValues]);

  const surveyPath = useMemo(
    () =>
      computeSurveyPath(interventionCallSurvey.steps, surveyAnswers, {
        customerAge: customer ? getCustomerAge() : 0,
        hasRecentInvestigationOrIntervention,
      }),
    [surveyAnswers, customer, getCustomerAge, hasRecentInvestigationOrIntervention]
  );

  const visibleSurveySteps = useMemo(() => getVisibleUserSteps(surveyPath), [surveyPath]);
  const terminalBranch = useMemo(() => getTerminalBranch(surveyPath), [surveyPath]);
  const showPhoneNumberField =
    terminalBranch?.action?.type === "GenerateTask" && Boolean(terminalBranch.action.taskCategory);

  const handleSurveyAnswerChange = useCallback(
    (stepId, value) => {
      const downstreamStepIds = getDownstreamStepIds(interventionCallSurvey.steps, stepId);
      const fieldsToReset = downstreamStepIds.map(getSurveyFieldName);

      if (stepId === "identify_asset_challenge") {
        fieldsToReset.push("survey.isWillPayImmediately");
      }

      form.setFieldsValue({
        ...fieldsToReset.reduce((acc, fieldName) => ({ ...acc, [fieldName]: undefined }), {}),
        [getSurveyFieldName(stepId)]: value,
      });
    },
    [form]
  );

  const handleSave = useCallback(() => {
    const values = form.getFieldsValue();
    const { nextCookingDate, phoneNumber } = values;

    const surveyValues = visibleSurveySteps
      .filter(item => item.branch)
      .map(({ step, branch }) => ({
        question: step.text,
        answer: branch.text,
      }));

    if (values["survey.isWillPayImmediately"]) {
      surveyValues.push({
        question: "Customer willing to pay immediately?",
        answer: values["survey.isWillPayImmediately"],
      });
    }

    const preparedData = {
      ...(terminalBranch?.action?.type === "GenerateTask" ? { childTask: terminalBranch.action.taskCategory } : {}),
      ...(nextCookingDate ? { nextCookingDate: getStartOfTheDay(nextCookingDate) } : {}),
      ...(phoneNumber ? { phoneNumber } : {}),
      survey: surveyValues,
    };
    setLoader(true);

    completeTask(data.id, preparedData)
      .then(async () => {
        setTimeout(() => {
          successMessage("Task was completed successfully");
          update();
          setLoader(false);
          onClose();
        }, 3000);
        if (selectedDate) {
          const { availableAt, timeSlot } = selectedDate || {};

          const availabilityData = {
            availableAt,
            timeSlot,
          };

          await setCustomerAvailabilityV2(customer.id, availabilityData);
        }
      })
      .catch(e => {
        setLoader(false);
        defaultNgiErrorMethod(e);
      });
  }, [data, form, selectedDate, visibleSurveySteps, terminalBranch, customer, update, onClose]);

  const onDelay = useCallback(() => {
    setLoader(true);
    closeConfirmationModal();
    delayTaskApi(data.id)
      .then(() => {
        setTimeout(() => {
          successMessage("Task delayed successfully");
          update();
          setLoader(false);
          onClose();
        }, 2000);
      })
      .catch(error => {
        defaultNgiErrorMethod(error);
        closeConfirmationModal(false);
        setLoader(false);
      });
  }, [data]);

  useEffect(() => {
    fetchCustomerById(data.customer.id).then(({ data }) => {
      setCustomer(data.result);
    });
    retrieveTasksArchive({
      customerId: data.customer.id,
      category: "Investigation",
      status: "Completed",
      pageSize: 500,
    }).then(res => {
      const {
        result: { items },
      } = res?.data;
      setLastInvestigationDate(items.length ? items[0].updatedAt : null);
    });
  }, []);

  const renderCustomerAvailability = useCallback(
    () => (
      <div className={classes.taskSettingsAvailability}>
        <div className={classes.taskPreviewName}>Schedule Availability</div>
        <CalendarPicker
          className={classes.calendarPicker}
          dayBoxClassName={classes.dayBox}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
      </div>
    ),
    [selectedDate, setSelectedDate]
  );

  const renderNextCookingDate = useCallback(
    () => (
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Next Cooking Date</div>
        <NGIFormItem preserve={false} name="nextCookingDate" rules={[...VALIDATION_RULES]}>
          <NGIDatePicker white disabledPasDates allowClear />
        </NGIFormItem>
      </div>
    ),
    []
  );

  const renderSurveyStep = useCallback(
    step => (
      <div className={classes.fieldItem} key={step.id}>
        <div className={classes.fieldQuestion}>
          <div className={classes.fieldQuestionTitle}>{step.text}</div>
        </div>
        <div className={classes.fieldAnswer}>
          <NGIFormItem
            preserve={false}
            name={getSurveyFieldName(step.id)}
            rules={[{ required: true, message: "Please select value" }]}
          >
            <NGISelect
              light
              placeholder="Please select value"
              fullWidth
              showSearch
              onChange={value => handleSurveyAnswerChange(step.id, value)}
            >
              {step.branches.map(branch => (
                <Option value={branch.text} key={branch.id}>
                  {branch.text}
                </Option>
              ))}
            </NGISelect>
          </NGIFormItem>
        </div>
      </div>
    ),
    [classes, handleSurveyAnswerChange]
  );

  const renderTerminalAction = useCallback(() => {
    if (!terminalBranch?.action) return null;

    const { action, id } = terminalBranch;

    if (action.type === "SetNextCookingDate") {
      return (
        <div className={classes.taskPreviewContainer}>
          <div className={classes.taskSettingsCardLost}>{renderNextCookingDate()}</div>
        </div>
      );
    }

    if (action.type === "GenerateTask") {
      const isCardLost = id === CARD_LOST_BRANCH_ID;

      return (
        <>
          {isCardLost && (
            <div className={classes.warnContainer}>
              Please ask <b>{data.customer.name}</b> to make a payment with prefix <b>C</b> for the lost card.
            </div>
          )}

          <div className={classes.taskPreviewContainer}>
            <div className={classes.taskPreviewName}>{`${action.taskCategory} Task`}</div>
            <Divider style={{ margin: "15px 0" }} />
            <div className={classes.taskSettings}>
              {!isCardLost && renderCustomerAvailability()}

              {isCardLost && (
                <div className={classes.taskSettingsCardLost}>
                  <div className={classes.section}>
                    <div className={classes.sectionTitle}>Customer willing to pay immediately?</div>
                    <NGIFormItem preserve={false} name="survey.isWillPayImmediately" rules={[...VALIDATION_RULES]}>
                      <NGISelect white placeholder="Please select value" fullWidth showSearch>
                        {["Yes", "No"].map(item => (
                          <Option value={item} key={item}>
                            {item}
                          </Option>
                        ))}
                      </NGISelect>
                    </NGIFormItem>
                  </div>
                  {isWillPayImmediately === "Yes" && renderNextCookingDate()}
                  {isWillPayImmediately === "No" && (
                    <div className={classes.section}>{renderCustomerAvailability()}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      );
    }

    return null;
  }, [
    terminalBranch,
    classes,
    data.customer.name,
    renderCustomerAvailability,
    renderNextCookingDate,
    isWillPayImmediately,
  ]);

  return (
    <Modal
      title={
        <div className={classes.titleWithDescription}>
          Intervention Call Task - {userFullName}
          <span>Please fill all mandatory fields</span>
        </div>
      }
      open={true}
      onCancel={onClose}
      closable={false}
      footer={[]}
    >
      <Spin spinning={isLoading}>
        <NGIForm
          name="executeTask"
          isUpdate
          form={form}
          onFinish={openConfirmationCompleteModal}
          className={classes.formContainer}
        >
          <div className={classes.modalContent}>
            <div className={classes.customerSection}>
              <div className={classes.customerName}>
                <CustomLink
                  className={classes.link}
                  underlined
                  target="_blank"
                  to={`/customers/customer/${data.customer.id}`}
                >
                  {`${data.customer.name} ${data.customer.id}`}
                </CustomLink>
              </div>
              <div className={classes.customerPhone}>
                <img src={phoneIcon} alt="phone" />+{data.customer.phone}
              </div>
            </div>

            <div className={classes.fieldsContainer}>
              {visibleSurveySteps.map(({ step }) => renderSurveyStep(step))}
              {renderTerminalAction()}

              {showPhoneNumberField && (
                <div className={classes.fieldItem}>
                  <div className={classes.fieldQuestion}>
                    <div className={classes.fieldQuestionTitle}>Task phone number (Optional)</div>
                  </div>
                  <div className={classes.fieldAnswer}>
                    <NGIFormItem
                      preserve={false}
                      name="phoneNumber"
                      rules={[
                        {
                          message: "Phone Number should be in the correct format",
                          validator: function (_, value) {
                            if (!value) return Promise.resolve();
                            const hasZero = value[3] === "0";
                            if (hasZero && value.match(/^(254|255)\d{10}$/)) {
                              return Promise.resolve();
                            } else if (!hasZero && value.match(/^(254|255)\d{9}$/)) {
                              return Promise.resolve();
                            }
                            return Promise.reject("Phone Number should be in the correct format");
                          },
                        },
                      ]}
                    >
                      <NGIInput light placeholder="(XXX)-XXX-XXX-XXX" />
                    </NGIFormItem>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className={classes.actionsContainer}>
            <CustomButton onClick={onClose} type="primary" color="outlined" size="small" text="Cancel" />

            <div className={classes.actions}>
              <CustomButton
                onClick={openConfirmationModal}
                type="primary"
                color="outlined"
                size="small"
                text="DELAY TASK"
              />
              <CustomButton htmlType="submit" type="primary" size="small" text="COMPLETE TASK" />
            </div>
          </div>
        </NGIForm>
      </Spin>
      {isConfirmationOpen && (
        <ConfirmationModal
          description={
            <p>
              Are you sure you want to Delay this task. It will be marked back as <b>Assigned</b> and will not be
              completed. Are you sure?
            </p>
          }
          onCancel={closeConfirmationModal}
          onConfirm={onDelay}
        />
      )}
      {isConfirmatioCompletenOpen && (
        <ConfirmationModal
          description="Are you sure you want to Complete this task?"
          onCancel={closeConfirmationCompleteModal}
          onConfirm={handleSave}
        />
      )}
    </Modal>
  );
};

export default ExecuteTaskModal;
